/**
 * useHeadWithdrawData Hook (파사드)
 * 본사 입출금내역 데이터 관리 훅 - 세부 훅들을 조합하여 단일 인터페이스 제공
 *
 * @description
 * - useWithdrawHistories: 입출금 내역 조회
 * - useWithdrawActions: 출금/입금 신청, 출금 가능 금액 조회
 * - useWithdrawMeta: 구분코드, 수수료, 계좌 정보
 * - useWithdrawOptions: 협력사/라이더 옵션 목록
 * - 엑셀 다운로드 및 출금 신청 내역 조회는 이 훅에서 인라인 유지
 */
import { useCallback, useState } from 'react';
import { headWithdrawApi } from '../api/headWithdrawApi';
import {
    HeadWithdrawRequestDetailDto,
    UseHeadWithdrawDataParams
} from '../types/headWithdraw.types';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { useWithdrawHistories } from './useWithdrawHistories';
import { useWithdrawActions } from './useWithdrawActions';
import { useWithdrawMeta } from './useWithdrawMeta';
import { useWithdrawOptions } from './useWithdrawOptions';

// re-export 타입 (기존 import 경로 유지)
export type { SupplierOption, RiderOption } from './useWithdrawOptions';

/**
 * 본사 입출금내역 데이터 훅 (파사드)
 * @param {UseHeadWithdrawDataParams} [params] - 훅 파라미터 (협력사 ID 등)
 * @returns {Object} 입출금 관련 상태 및 함수
 */
export const useHeadWithdrawData = (params?: UseHeadWithdrawDataParams) => {
    const { showToast } = useToast();
    const [downloading, setDownloading] = useState(false);
    const [withdrawRequests, setWithdrawRequests] = useState<HeadWithdrawRequestDetailDto[]>([]);

    // 세부 훅
    const {
        loading: historiesLoading,
        histories,
        currentBalanceByRider,
        currentBalanceBySupplier,
        fetchHistories,
    } = useWithdrawHistories(params);

    const {
        loading: actionsLoading,
        withdrawableAmount,
        fetchWithdrawableAmount,
        requestWithdraw,
        requestDeposit,
    } = useWithdrawActions();

    const {
        wdTypes,
        withdrawFee,
        depositFee,
        accountInfo,
        fetchAccountInfo,
    } = useWithdrawMeta();

    const {
        supplierOptions,
        riderOptions,
        fetchSupplierOptions,
        fetchRiderOptions,
    } = useWithdrawOptions();

    // loading은 histories 또는 actions 중 하나라도 로딩 중이면 true
    const loading = historiesLoading || actionsLoading;

    /**
     * 출금 신청 내역 조회
     */
    const fetchWithdrawRequests = useCallback(async (
        status?: 'pending' | 'approved' | 'rejected' | 'completed'
    ) => {
        try {
            const requests = await headWithdrawApi.getWithdrawRequestList(
                params?.supplierId,
                status
            );
            setWithdrawRequests(requests);
        } catch {
            showToast('출금 신청 내역을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setWithdrawRequests([]);
        }
    }, [params?.supplierId, showToast]);

    /**
     * 엑셀 다운로드 (독립적 기능, 여기에 인라인 유지)
     */
    const downloadExcel = useCallback(async (
        fromDate: string,
        toDate: string,
        wdType?: string,
        reqType?: string,
        supplierId?: string,
        supplierName?: string,
        riderId?: string,
        riderName?: string
    ) => {
        try {
            setDownloading(true);
            const blob = await headWithdrawApi.downloadWithdrawHistoryExcel(
                fromDate,
                toDate,
                wdType,
                reqType,
                supplierId,
                supplierName,
                riderId,
                riderName
            );

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `입출금내역_${fromDate.replace(/-/g, '')}_${toDate.replace(/-/g, '')}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showToast('엑셀 파일이 다운로드되었습니다.', { type: ToastType.SUCCESS });
        } catch {
            showToast('엑셀 다운로드에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setDownloading(false);
        }
    }, [showToast]);

    return {
        loading,
        downloading,
        histories,
        currentBalanceByRider,
        currentBalanceBySupplier,
        withdrawableAmount,
        withdrawRequests,
        wdTypes,
        withdrawFee,
        depositFee,
        accountInfo,
        supplierOptions,
        riderOptions,
        fetchHistories,
        fetchWithdrawableAmount,
        requestWithdraw,
        requestDeposit,
        fetchWithdrawRequests,
        fetchAccountInfo,
        fetchSupplierOptions,
        fetchRiderOptions,
        refreshHistories: fetchHistories,
        refreshWithdrawableAmount: fetchWithdrawableAmount,
        refreshWithdrawRequests: fetchWithdrawRequests,
        downloadExcel,
    };
};
